import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { searchFieldsMatch } from '../utils/searchText';

/**
 * Select mm-* : sheet mobile / menu glass desktop (navigation clavier).
 */
export default function MmSelect({
  value = '',
  onChange,
  options = [],
  placeholder = 'Sélectionner…',
  disabled = false,
  required = false,
  name,
  id: idProp,
  className = '',
  emptyLabel,
  searchable = false,
  searchPlaceholder = 'Rechercher…',
}) {
  const autoId = useId();
  const id = idProp || autoId;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(-1);
  const triggerRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const desktopMenuRef = useRef(null);
  const searchRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  const items = useMemo(
    () =>
      (Array.isArray(options) ? options : []).map((opt) =>
        typeof opt === 'string' || typeof opt === 'number'
          ? { value: String(opt), label: String(opt) }
          : {
              value: String(opt?.value ?? ''),
              label: String(opt?.label ?? opt?.value ?? ''),
              hint: opt?.hint ? String(opt.hint) : '',
              disabled: Boolean(opt?.disabled),
            }
      ),
    [options]
  );

  const enableSearch = searchable || items.length > 8;

  const filtered = useMemo(() => {
    if (!enableSearch || !String(query || '').trim()) return items;
    return items.filter((o) => searchFieldsMatch(query, [o.label, o.hint, o.value]));
  }, [items, query, enableSearch]);

  const selectable = useMemo(
    () => filtered.filter((o) => !o.disabled),
    [filtered]
  );

  const selected = items.find((o) => o.value === String(value ?? '')) || null;

  const close = () => {
    setOpen(false);
    setQuery('');
    setHighlight(-1);
  };

  const pick = (next) => {
    if (disabled) return;
    onChange?.(next);
    close();
  };

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        triggerRef.current?.focus?.();
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!selectable.length) return;
        setHighlight((prev) => {
          if (prev < 0) {
            return e.key === 'ArrowDown' ? 0 : selectable.length - 1;
          }
          const delta = e.key === 'ArrowDown' ? 1 : -1;
          return (prev + delta + selectable.length) % selectable.length;
        });
        return;
      }
      if (e.key === 'Enter' && highlight >= 0 && selectable[highlight]) {
        e.preventDefault();
        pick(selectable[highlight].value);
      }
    };
    const onPointer = (e) => {
      const t = e.target;
      if (
        triggerRef.current?.contains(t) ||
        mobileMenuRef.current?.contains(t) ||
        desktopMenuRef.current?.contains(t)
      ) {
        return;
      }
      close();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onPointer);
    window.addEventListener('touchstart', onPointer, { passive: true });
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onPointer);
      window.removeEventListener('touchstart', onPointer);
    };
  }, [open, highlight, selectable]);

  useEffect(() => {
    if (!open) return;
    const idx = selectable.findIndex((o) => o.value === String(value ?? ''));
    setHighlight(idx >= 0 ? idx : selectable.length ? 0 : -1);
  }, [open, value, selectable]);

  useEffect(() => {
    if (!open || !enableSearch) return undefined;
    const t = window.setTimeout(() => searchRef.current?.focus?.(), 80);
    return () => window.clearTimeout(t);
  }, [open, enableSearch]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;

    const place = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const isMobile = window.matchMedia('(max-width: 639px)').matches;
      if (isMobile) {
        setMenuStyle(null);
        return;
      }
      const maxH = Math.min(enableSearch ? 380 : 300, window.innerHeight - 24);
      const spaceBelow = window.innerHeight - rect.bottom - 12;
      const openUp = spaceBelow < 220 && rect.top > spaceBelow;
      const width = Math.min(Math.max(rect.width, 280), window.innerWidth - 16);
      setMenuStyle({
        position: 'fixed',
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        width,
        maxHeight: maxH,
        zIndex: 120,
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + 8 }
          : { top: rect.bottom + 8 }),
      });
    };

    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, enableSearch]);

  const searchField = enableSearch ? (
    <div className="mm-select-search sticky top-0 z-[1]">
      <input
        ref={searchRef}
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={searchPlaceholder}
        className="mm-field !min-h-[42px] text-sm"
        autoComplete="off"
      />
    </div>
  ) : null;

  const list = (
    <ul className="mm-select-list" role="listbox" aria-labelledby={id}>
      {!required && placeholder && !query ? (
        <li>
          <button
            type="button"
            role="option"
            aria-selected={!selected}
            className={`mm-select-option ${!selected ? 'mm-select-option--active' : ''}`}
            onClick={() => pick('')}
          >
            <span className="text-slate-500">{emptyLabel || placeholder}</span>
          </button>
        </li>
      ) : null}
      {filtered.length === 0 ? (
        <li className="px-3 py-6 text-center text-sm text-slate-500">Aucun résultat</li>
      ) : (
        filtered.map((opt) => {
          const active = selected && opt.value === selected.value;
          const hi = selectable.findIndex((s) => s.value === opt.value) === highlight;
          return (
            <li key={opt.value || opt.label}>
              <button
                type="button"
                role="option"
                aria-selected={active}
                disabled={opt.disabled}
                className={`mm-select-option ${active ? 'mm-select-option--active' : ''} ${
                  hi ? 'mm-select-option--focus' : ''
                }`}
                onMouseEnter={() => {
                  const i = selectable.findIndex((s) => s.value === opt.value);
                  if (i >= 0) setHighlight(i);
                }}
                onClick={() => pick(opt.value)}
              >
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-[#0f3350]">{opt.label}</span>
                  {opt.hint ? (
                    <span className="mt-0.5 block truncate text-xs text-slate-500">{opt.hint}</span>
                  ) : null}
                </span>
                {active ? (
                  <svg
                    className="h-4 w-4 shrink-0 text-[#2f6fd4]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : null}
              </button>
            </li>
          );
        })
      )}
    </ul>
  );

  const portal =
    open && typeof document !== 'undefined'
      ? createPortal(
          <>
            <div className="fixed inset-0 z-[110] sm:hidden" role="presentation">
              <button
                type="button"
                className="mm-modal-backdrop absolute inset-0"
                aria-label="Fermer"
                onClick={close}
              />
              <div
                ref={mobileMenuRef}
                className="mm-modal-panel absolute inset-x-0 bottom-0 flex max-h-[min(78dvh,520px)] flex-col overflow-hidden p-0"
                style={{ marginBottom: 0 }}
              >
                <div className="flex shrink-0 justify-center pt-2" aria-hidden>
                  <span className="mm-modal-handle" />
                </div>
                <div className="mm-modal-header !pb-2">
                  <p className="font-display text-sm font-extrabold text-[#0f3350]">{placeholder}</p>
                  <button
                    type="button"
                    onClick={close}
                    className="mm-press flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100/80 text-slate-500"
                    aria-label="Fermer"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                {searchField}
                <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))]">
                  {list}
                </div>
              </div>
            </div>

            <div
              ref={desktopMenuRef}
              className="mm-select-menu mm-select-menu--enter hidden overflow-y-auto sm:flex sm:flex-col"
              style={menuStyle || { display: 'none' }}
              role="presentation"
            >
              {searchField}
              <div className="min-h-0 flex-1 overflow-y-auto px-1 pb-1">{list}</div>
            </div>
          </>,
          document.body
        )
      : null;

  return (
    <div className={`mm-select ${className}`.trim()}>
      {name ? <input type="hidden" name={name} value={value ?? ''} required={required} /> : null}
      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-required={required || undefined}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={`mm-field mm-field--select text-left ${open ? 'mm-field--open' : ''} ${
          !selected ? 'mm-field--placeholder' : ''
        }`}
      >
        <span className="min-w-0 flex-1 truncate">{selected ? selected.label : placeholder}</span>
        <svg
          className={`mm-select-chevron ${open ? 'mm-select-chevron--open' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {portal}
    </div>
  );
}
